'use client';

import { useEffect, useState } from 'react';

/**
 * A queue of user prompts the panel accepts WHILE the assistant is still
 * streaming. The textarea isn't blocked during a stream — submissions push
 * onto the queue and the head is flushed (dispatched as a real send) the
 * moment `isStreaming` flips false.
 *
 * The flush effect lives in this hook (not in the panel) because the guide
 * forbids raw `useEffect` in a component but allows the same effect when it
 * is isolated behind a clean hook API. The hook exposes:
 *   - `queue` (read-only count source for UI),
 *   - `enqueue(text)` to push during a stream,
 *   - `forceFlush()` to drain immediately (used by "send now" / stop).
 *
 * The flush trigger is genuinely the "stream finished" event surfaced via the
 * `isStreaming` transition — there's no Chat-level hook reachable from the
 * panel scope, so the subscription effect is the right escape hatch.
 */
export function usePendingPromptQueue(input: {
  isStreaming: boolean;
  dispatch: (text: string) => void;
}): {
  queue: ReadonlyArray<string>;
  enqueue: (text: string) => void;
  drain: () => string[];
  clear: () => void;
  removeAt: (index: number) => void;
} {
  const [queue, setQueue] = useState<string[]>([]);
  useEffect(() => {
    if (input.isStreaming) return;
    if (queue.length === 0) return;
    const [head, ...rest] = queue;
    if (!head) return;
    setQueue(rest);
    input.dispatch(head);
    // `dispatch` closes over panel state that changes each render, but only
    // the latest reference matters for the next flush — re-firing on every
    // dispatch identity change would just re-run the same flush.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.isStreaming, queue]);
  return {
    queue,
    enqueue: text => setQueue(q => q.length >= 2 ? q : [...q, text]),
    drain: () => {
      const snapshot = [...queue];
      setQueue([]);
      return snapshot;
    },
    clear: () => setQueue([]),
    removeAt: index => setQueue(q => q.filter((_, i) => i !== index))
  };
}